import React, { useState, useEffect } from 'react';
import './Sales.css';
import Navigation from '../../Components/Navigation';
import { FiSearch, FiDownload, FiRefreshCw, FiX } from 'react-icons/fi';
import { FaEye, FaFilePdf } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const Sales = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [salesData, setSalesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ads form data state
  const [adsData, setAdsData] = useState([]);
  const [filteredAdsData, setFilteredAdsData] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState(null);
  const [adsSearchTerm, setAdsSearchTerm] = useState('');
  const [adsSortOrder, setAdsSortOrder] = useState('desc');
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('submissionDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  
  // Export filter modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    planFilter: 'all',
    dateFilter: 'all',
    monthFilter: '',
    yearFilter: '',
    searchTerm: '',
    selectedColumns: {
      fullName: true,
      email: true,
      contact: true,
      pan: true,
      address: true,
      plan: true,
      amount: true,
      agent: true,
      date: true
    }
  });

  // Define all available columns
  const availableColumns = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'contact', label: 'Contact Number' },
    { key: 'pan', label: 'PAN Number' },
    { key: 'address', label: 'Address' },
    { key: 'plan', label: 'Plan' },
    { key: 'amount', label: 'Amount' },
    { key: 'agent', label: 'Agent Name' },
    { key: 'date', label: 'Transaction Date' }
  ];

  // Month names
  const monthNames = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Generate year options from 2024 to 2030
  const generateYearOptions = () => {
    const years = [];
    for (let year = 2024; year <= 2030; year++) {
      years.push(year);
    }
    return years;
  };

  const yearOptions = generateYearOptions();

  // Fetch sales data from API
  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://tstock.trillionstechsolutions.com/payment/get-all-details');
      const data = await response.json();
      
      if (data.success) {
        console.log('Raw API data:', data.data);
        
        const transformedData = data.data.flatMap(payment => {
          if (!payment.transactionDetails || !Array.isArray(payment.transactionDetails)) {
            console.warn('Payment missing transactionDetails:', payment);
            return [];
          }
          
          return payment.transactionDetails.map(transaction => ({
            _id: transaction._id,
            salesId: transaction._id || transaction.transactionId || '',
            fullName: payment.fullName || '',
            emailAddress: payment.emailAddress || '',
            contactNumber: payment.contactNumber || '',
            panNumber: payment.panNumber || '',
            address: payment.address || '',
            plan: transaction.plan || '',
            planAmount: transaction.amount ? `₹${transaction.amount}` : '',
            agentName: transaction.agentName || '',
            submissionDate: transaction.transactionDate || transaction.date || '',
            submissionDateFormatted: transaction.transactionDate || transaction.date || '',
            createdAt: payment.createdAt || '',
            updatedAt: payment.updatedAt || ''
          }));
        });
        
        console.log('Transformed data:', transformedData);
        console.log(`Total records: ${transformedData.length}`);
        
        setSalesData(transformedData);
        setFilteredData(transformedData);
      } else {
        setError('Failed to fetch sales data');
      }
    } catch (err) {
      console.error('Error fetching sales data:', err);
      setError('Error fetching sales data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, []);

  const fetchAdsData = async () => {
    try {
      setAdsLoading(true);
      setAdsError(null);
      const response = await fetch('https://tstock.trillionstechsolutions.com/ads-registration/all');
      const data = await response.json();

      if (data.success) {
        setAdsData(data.data || []);
        setFilteredAdsData(data.data || []);
      } else {
        setAdsError('Failed to fetch ads form data');
      }
    } catch (err) {
      console.error('Error fetching ads form data:', err);
      setAdsError('Error fetching ads form data: ' + err.message);
    } finally {
      setAdsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ads') {
      fetchAdsData();
    }
  }, [activeTab]);

  useEffect(() => {
    let filtered = [...adsData];

    if (adsSearchTerm) {
      const term = adsSearchTerm.toLowerCase();
      filtered = filtered.filter(ad =>
        (ad.name || '').toLowerCase().includes(term) ||
        (ad.email || '').toLowerCase().includes(term) ||
        (ad.mobileNumber || '').includes(adsSearchTerm) ||
        (ad.market || '').toLowerCase().includes(term) ||
        (ad.capital || '').toLowerCase().includes(term)
      );
    }

    filtered.sort((a, b) => {
      const aDate = new Date(a.registrationDate);
      const bDate = new Date(b.registrationDate);
      return adsSortOrder === 'desc' ? bDate - aDate : aDate - bDate;
    });

    setFilteredAdsData(filtered);
  }, [adsData, adsSearchTerm, adsSortOrder]);

  const formatAdsDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter and search logic
  useEffect(() => {
    let filtered = [...salesData];

    if (searchTerm) {
      filtered = filtered.filter(sale => 
        sale.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.emailAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.salesId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.plan.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (planFilter !== 'all') {
      filtered = filtered.filter(sale => sale.plan === planFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(sale => {
        const dateParts = sale.submissionDate.split('/');
        const saleDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
        
        switch (dateFilter) {
          case 'today':
            return saleDate >= today;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return saleDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return saleDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'submissionDate') {
        const aDateParts = aValue.split('/');
        const bDateParts = bValue.split('/');
        aValue = new Date(aDateParts[2], aDateParts[1] - 1, aDateParts[0]);
        bValue = new Date(bDateParts[2], bDateParts[1] - 1, bDateParts[0]);
      } else if (sortBy === 'planAmount') {
        aValue = parseFloat(aValue.replace(/[₹,]/g, ''));
        bValue = parseFloat(bValue.replace(/[₹,]/g, ''));
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredData(filtered);
  }, [salesData, searchTerm, planFilter, dateFilter, sortBy, sortOrder]);

  const uniquePlans = [...new Set(salesData.map(sale => sale.plan))];

  const handleRefresh = () => {
    if (activeTab === 'ads') {
      fetchAdsData();
    } else {
      fetchSalesData();
    }
  };

  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSale(null);
  };

  // Function to get next invoice number from Firestore
  const getNextInvoiceNumber = async () => {
    try {
      const invoiceCounterRef = doc(db, 'invoiceCounter', 'counter');
      const counterDoc = await getDoc(invoiceCounterRef);
      
      let nextNumber;
      if (counterDoc.exists()) {
        const currentNumber = counterDoc.data().lastInvoiceNumber;
        nextNumber = currentNumber + 1;
        await updateDoc(invoiceCounterRef, {
          lastInvoiceNumber: nextNumber
        });
      } else {
        nextNumber = 777334;
        await setDoc(invoiceCounterRef, {
          lastInvoiceNumber: nextNumber
        });
      }
      
      return nextNumber;
    } catch (error) {
      console.error('Error getting invoice number:', error);
      return Date.now().toString().slice(-8);
    }
  };

  const handleGenerateInvoice = async (sale) => {
    try {
      setLoading(true);
      
      const invoiceNumber = await getNextInvoiceNumber();
      const pdfDoc = new jsPDF();
      const pageWidth = 210;
      let yPos = 10;
      
      // ===== HEADER SECTION =====
      pdfDoc.setFontSize(16);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setTextColor(0, 0, 139);
      pdfDoc.text('Trillion Stock Research', pageWidth / 2, yPos + 6, { align: 'center' });
      
      yPos += 10;
      pdfDoc.setDrawColor(0, 0, 139);
      pdfDoc.setLineWidth(0.3);
      pdfDoc.line(15, yPos, pageWidth - 15, yPos);
      
      yPos += 8;
      pdfDoc.setFontSize(9);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setTextColor(0, 0, 0);
      pdfDoc.text(`Date of Invoice: ${new Date().toLocaleDateString('en-US')}`, 15, yPos);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text(`Invoice No.: ${invoiceNumber}`, pageWidth - 15, yPos, { align: 'right' });
      
      // ===== CLIENT DETAILS SECTION =====
      yPos += 10;
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Client Details', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 6;
      pdfDoc.setFontSize(9);
      pdfDoc.setFont('helvetica', 'normal');
      
      pdfDoc.text(`Client: ${sale.fullName}`, 15, yPos);
      pdfDoc.text(`Pan No. ${sale.panNumber}`, pageWidth - 15, yPos, { align: 'right' });
      
      yPos += 5;
      const addressText = sale.address || 'Address not provided';
      pdfDoc.text(`Address: ${addressText}`, 15, yPos);
      
      yPos += 5;
      pdfDoc.text(`Mobile No.: ${sale.contactNumber}`, 15, yPos);
      
      yPos += 5;
      pdfDoc.text(`Email: ${sale.emailAddress}`, 15, yPos);
      
      // ===== SERVICE DETAILS SECTION =====
      yPos += 10;
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Service Details', pageWidth / 2, yPos, { align: 'center' });

      // Define column positions
      const col1X = 15;      // Sr. No.
      const col2X = 30;      // Product Description
      const col3X = 70;      // Duration of Service
      const col4X = 118;     // Paid
      const col5X = 138;     // Taxable Value
      const col6X = 162;     // Tax
      const col7X = 182;     // Total Amount

      const rowHeight = 14;
      const headerY = yPos + 6;

      // Calculate values
      const paidAmount = parseFloat(sale.planAmount?.replace(/[₹,]/g, '') || 0);
      const taxableValue = Math.round(paidAmount / 1.36);
      const totalAmount = paidAmount;

      // DRAW TABLE HEADER
      pdfDoc.setDrawColor(0, 0, 0);
      pdfDoc.setLineWidth(0.3);

      // Draw header row border
      pdfDoc.rect(col1X, headerY - 3, pageWidth - col1X - 15, rowHeight);

      // Draw vertical lines in header
      pdfDoc.line(col2X, headerY - 3, col2X, headerY + rowHeight - 3);
      pdfDoc.line(col3X, headerY - 3, col3X, headerY + rowHeight - 3);
      pdfDoc.line(col4X, headerY - 3, col4X, headerY + rowHeight - 3);
      pdfDoc.line(col5X, headerY - 3, col5X, headerY + rowHeight - 3);
      pdfDoc.line(col6X, headerY - 3, col6X, headerY + rowHeight - 3);
      pdfDoc.line(col7X, headerY - 3, col7X, headerY + rowHeight - 3);

      // Header text - CENTERED
      pdfDoc.setFontSize(8);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Sr. No.', (col1X + col2X) / 2, headerY + 4, { align: 'center' });
      pdfDoc.text('Product', (col2X + col3X) / 2, headerY + 1, { align: 'center' });
      pdfDoc.text('Description', (col2X + col3X) / 2, headerY + 5, { align: 'center' });
      pdfDoc.text('Duration of', (col3X + col4X) / 2, headerY + 1, { align: 'center' });
      pdfDoc.text('Service', (col3X + col4X) / 2, headerY + 5, { align: 'center' });
      pdfDoc.text('Paid', (col4X + col5X) / 2, headerY + 4, { align: 'center' });
      pdfDoc.text('Taxable', (col5X + col6X) / 2, headerY + 1, { align: 'center' });
      pdfDoc.text('Value', (col5X + col6X) / 2, headerY + 5, { align: 'center' });
      pdfDoc.text('Tax', (col6X + col7X) / 2, headerY + 4, { align: 'center' });
      pdfDoc.text('Total', (col7X + pageWidth - 15) / 2, headerY + 1, { align: 'center' });
      pdfDoc.text('Amount', (col7X + pageWidth - 15) / 2, headerY + 5, { align: 'center' });

      // DRAW DATA ROW
      const dataY = headerY + rowHeight;
      const taxRowHeight = rowHeight / 3;

      // Draw outer rectangle for entire data row
      pdfDoc.rect(col1X, dataY, pageWidth - col1X - 15, rowHeight);

      // Draw vertical lines for data row
      pdfDoc.line(col2X, dataY, col2X, dataY + rowHeight);
      pdfDoc.line(col3X, dataY, col3X, dataY + rowHeight);
      pdfDoc.line(col4X, dataY, col4X, dataY + rowHeight);
      pdfDoc.line(col5X, dataY, col5X, dataY + rowHeight);
      pdfDoc.line(col6X, dataY, col6X, dataY + rowHeight);
      pdfDoc.line(col7X, dataY, col7X, dataY + rowHeight);

      // Draw horizontal lines inside Tax column (3 divisions)
      pdfDoc.line(col6X, dataY + taxRowHeight, col7X, dataY + taxRowHeight);
      pdfDoc.line(col6X, dataY + (taxRowHeight * 2), col7X, dataY + (taxRowHeight * 2));

      // Data text - CENTERED IN CELLS
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setFontSize(9);

      // Sr. No. - CENTERED
      pdfDoc.text('1', (col1X + col2X) / 2, dataY + 7, { align: 'center' });

      // Product Description - CENTERED
      const planText = sale.plan || 'customized';
      pdfDoc.text(planText, (col2X + col3X) / 2, dataY + 7, { align: 'center' });

      // Duration - CENTERED
      pdfDoc.text('From : one Month', (col3X + col4X) / 2, dataY + 7, { align: 'center' });

      // Paid - CENTERED
      pdfDoc.text(paidAmount.toString(), (col4X + col5X) / 2, dataY + 7, { align: 'center' });

      // Taxable Value - CENTERED
      pdfDoc.text(taxableValue.toString(), (col5X + col6X) / 2, dataY + 7, { align: 'center' });

      // Tax labels - LEFT ALIGNED in Tax column
      pdfDoc.setFontSize(8);
      pdfDoc.text('CGST', col6X + 2, dataY + (taxRowHeight * 0.5) + 1.5);
      pdfDoc.text('SGST', col6X + 2, dataY + (taxRowHeight * 1.5) + 1.5);
      pdfDoc.text('IGST', col6X + 2, dataY + (taxRowHeight * 2.5) + 1.5);

      // Total Amount - CENTERED and BOLD
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text(totalAmount.toString(), (col7X + pageWidth - 15) / 2, dataY + 7, { align: 'center' });

      // Update yPos for next section
      yPos = dataY + rowHeight + 2;

      // TOTAL INVOICE AMOUNT ROW
      pdfDoc.setLineWidth(0.3);
      pdfDoc.rect(col1X, yPos, pageWidth - col1X - 15, 8);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setFontSize(9);

      // Convert amount to words
      const convertNumberToWords = (num) => {
        const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
        
        if (num === 0) return 'zero';
        if (num < 20) return ones[num];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
        if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + convertNumberToWords(num % 100) : '');
        if (num < 100000) return convertNumberToWords(Math.floor(num / 1000)) + ' thousand' + (num % 1000 ? ' ' + convertNumberToWords(num % 1000) : '');
        return convertNumberToWords(Math.floor(num / 100000)) + ' lakh' + (num % 100000 ? ' ' + convertNumberToWords(num % 100000) : '');
      };

      const amountInWords = convertNumberToWords(totalAmount);
      const capitalizedWords = amountInWords.charAt(0).toUpperCase() + amountInWords.slice(1);
      pdfDoc.text(`Total Invoice Amount in words: "${capitalizedWords}."`, col1X + 3, yPos + 5.5);

      yPos += 10;
      
      // ===== BANK DETAILS SECTION =====
      yPos += 12;
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Bank Details', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 6;
      pdfDoc.setFontSize(9);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.text('Bank Name: BANK OF BARODA', 15, yPos);
      
      yPos += 5;
      pdfDoc.text('Account No.: 41910200000519', 15, yPos);
      
      yPos += 5;
      pdfDoc.text('Branch: PALDA INDORE', 15, yPos);
      
      // ===== IMPORTANT NOTES SECTION =====
      yPos += 12;
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Important Notes*', 15, yPos);
      
      yPos += 6;
      pdfDoc.setFontSize(8);
      pdfDoc.setFont('helvetica', 'normal');
      
      const importantNotes = [
        'Investments in securities market are subject to market risks. Read all the related documents carefully before investing.',
        'Registration granted by SEBI, enlistment as RA with exchange and certification from NISM in no way guarantee performance of the intermediary or provide any assurance of returns to investors.',
        'Never Trade Without Stop Loss.',
        'Please read all terms and conditions and Refund Policy carefully on our website: www.trillionstockresearch.com.',
        'If you have any queries concerning this invoice Contact us at: +919977555378'
      ];
      
      importantNotes.forEach((note, index) => {
        const lines = pdfDoc.splitTextToSize(`${index + 1}. ${note}`, pageWidth - 30);
        lines.forEach((line) => {
          pdfDoc.text(line, 15, yPos);
          yPos += 4;
        });
        yPos += 2;
      });
      
      // ===== FOOTER =====
      yPos += 8;
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100, 100, 100);
      pdfDoc.text('This invoice is computer generated and does not require signature.', pageWidth / 2, yPos, { align: 'center' });
      
      const fileName = `invoice-${sale.fullName.replace(/\s+/g, '-')}-${invoiceNumber}.pdf`;
      pdfDoc.save(fileName);
      
      console.log('Invoice generated for:', sale.fullName, 'Invoice Number:', invoiceNumber);
      setLoading(false);
    } catch (error) {
      console.error('Error generating invoice:', error);
      setError('Failed to generate invoice. Please try again.');
      setLoading(false);
    }
  };

  const handleExport = () => {
    setIsExportModalOpen(true);
  };

  const handleExportWithFilters = () => {
    try {
      let exportData = [...salesData];
      
      if (exportFilters.searchTerm) {
        exportData = exportData.filter(sale => 
          sale.fullName.toLowerCase().includes(exportFilters.searchTerm.toLowerCase()) ||
          sale.emailAddress.toLowerCase().includes(exportFilters.searchTerm.toLowerCase()) ||
          sale.salesId.toLowerCase().includes(exportFilters.searchTerm.toLowerCase()) ||
          sale.agentName.toLowerCase().includes(exportFilters.searchTerm.toLowerCase()) ||
          sale.plan.toLowerCase().includes(exportFilters.searchTerm.toLowerCase())
        );
      }
      
      if (exportFilters.planFilter !== 'all') {
        exportData = exportData.filter(sale => sale.plan === exportFilters.planFilter);
      }
      
      if (exportFilters.dateFilter !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        exportData = exportData.filter(sale => {
          const dateParts = sale.submissionDate.split('/');
          const saleDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
          
          switch (exportFilters.dateFilter) {
            case 'today':
              return saleDate >= today;
            case 'week':
              const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
              return saleDate >= weekAgo;
            case 'month':
              const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
              return saleDate >= monthAgo;
            default:
              return true;
          }
        });
      }

      if (exportFilters.monthFilter) {
        exportData = exportData.filter(sale => {
          const dateParts = sale.submissionDate.split('/');
          const saleMonth = dateParts[1];
          return saleMonth === exportFilters.monthFilter;
        });
      }

      if (exportFilters.yearFilter) {
        exportData = exportData.filter(sale => {
          const dateParts = sale.submissionDate.split('/');
          const saleYear = dateParts[2];
          return saleYear === exportFilters.yearFilter;
        });
      }
      
      const totalAmount = exportData.reduce((sum, sale) => {
        const amount = parseFloat(sale.planAmount?.replace(/[₹,]/g, '') || '0');
        return sum + amount;
      }, 0);
      
      const headers = [];
      const headerMap = {
        fullName: 'Full Name',
        email: 'Email',
        contact: 'Contact Number',
        pan: 'PAN Number',
        address: 'Address',
        plan: 'Plan',
        amount: 'Amount',
        agent: 'Agent Name',
        date: 'Transaction Date'
      };
      
      availableColumns.forEach(col => {
        if (exportFilters.selectedColumns[col.key]) {
          headers.push(headerMap[col.key]);
        }
      });
      
      const excelData = [headers];
      
      exportData.forEach((sale) => {
        const row = [];
        
        availableColumns.forEach(col => {
          if (exportFilters.selectedColumns[col.key]) {
            switch(col.key) {
              case 'fullName':
                row.push(sale.fullName || '');
                break;
              case 'email':
                row.push(sale.emailAddress || '');
                break;
              case 'contact':
                row.push(sale.contactNumber || '');
                break;
              case 'pan':
                row.push(sale.panNumber || '');
                break;
              case 'address':
                row.push(sale.address || '');
                break;
              case 'plan':
                row.push(sale.plan || '');
                break;
              case 'amount':
                row.push(sale.planAmount || '');
                break;
              case 'agent':
                row.push(sale.agentName || '');
                break;
              case 'date':
                row.push(sale.submissionDateFormatted || sale.submissionDate || '');
                break;
              default:
                break;
            }
          }
        });
        
        excelData.push(row);
      });
      
      excelData.push([]);
      
      const summaryRow = new Array(headers.length).fill('');
      const amountIndex = headers.indexOf('Amount');
      if (amountIndex !== -1) {
        summaryRow[amountIndex - 1] = 'Total:';
        summaryRow[amountIndex] = `₹${totalAmount.toLocaleString('en-IN')}`;
      }
      summaryRow[summaryRow.length - 1] = `Total Records: ${exportData.length}`;
      excelData.push(summaryRow);
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      const widthMap = {
        'Full Name': 25,
        'Email': 30,
        'Contact Number': 15,
        'PAN Number': 15,
        'Address': 40,
        'Plan': 20,
        'Amount': 15,
        'Agent Name': 20,
        'Transaction Date': 18
      };
      
      ws['!cols'] = headers.map(header => ({ wch: widthMap[header] || 20 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Data');
      
      const fileName = `sales-data-${exportFilters.dateFilter}-${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
      console.log(`Exported ${exportData.length} records with total ₹${totalAmount.toLocaleString('en-IN')} to ${fileName}`);
      
      setIsExportModalOpen(false);
      setExportFilters({ 
        planFilter: 'all', 
        dateFilter: 'all', 
        monthFilter: '',
        yearFilter: '',
        searchTerm: '',
        selectedColumns: {
          fullName: true,
          email: true,
          contact: true,
          pan: true,
          address: true,
          plan: true,
          amount: true,
          agent: true,
          date: true
        }
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setExportFilters({ 
      planFilter: 'all', 
      dateFilter: 'all', 
      monthFilter: '',
      yearFilter: '',
      searchTerm: '',
      selectedColumns: {
        fullName: true,
        email: true,
        contact: true,
        pan: true,
        address: true,
        plan: true,
        amount: true,
        agent: true,
        date: true
      }
    });
  };

  return (
    <div className="page-container">
      <Navigation />
      <div className="sales-container">
        <div className="sales-header">
          <h1 className="sales-title">Sales Management</h1>
          <div className="sales-actions">
            <button onClick={handleRefresh} className="refresh-btn">
              <FiRefreshCw /> Refresh
            </button>
            {activeTab === 'sales' && (
              <button onClick={handleExport} className="export-btn">
                <FiDownload /> Export Excel
              </button>
            )}
          </div>
        </div>

        <div className="sales-tabs">
          <button
            type="button"
            className={`sales-tab ${activeTab === 'sales' ? 'active' : ''}`}
            onClick={() => setActiveTab('sales')}
          >
            Sales Data
          </button>
          <button
            type="button"
            className={`sales-tab ${activeTab === 'ads' ? 'active' : ''}`}
            onClick={() => setActiveTab('ads')}
          >
            Ads Form Data
          </button>
        </div>

        {activeTab === 'sales' && loading && (
          <div className="loading-container">
            <FiRefreshCw className="loading-spinner" />
            <p>Loading sales data...</p>
          </div>
        )}

        {activeTab === 'sales' && !loading && error && (
          <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={handleRefresh} className="retry-btn">
              <FiRefreshCw /> Retry
            </button>
          </div>
        )}

        {activeTab === 'sales' && !loading && !error && (
          <>
        <div className="filters-section">
          <div className="search-container">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, sales ID, agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <div className="filter-group">
              <label>Plan:</label>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Plans</option>
                {uniquePlans.map(plan => (
                  <option key={plan} value={plan}>{plan}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Date:</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="submissionDate">Submission Date</option>
                <option value="planAmount">Amount</option>
                <option value="fullName">Name</option>
                <option value="plan">Plan</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Order:</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="filter-select"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>

        <div className="stats-container">
          <div className="stat-card">
            <h3>Total Sales</h3>
            <p className="stat-number">{filteredData.length}</p>
          </div>
          <div className="stat-card">
            <h3>Total Revenue</h3>
            <p className="stat-number">
              ₹{filteredData.reduce((sum, sale) => 
                sum + parseFloat(sale.planAmount.replace(/[₹,]/g, '')), 0
              ).toLocaleString()}
            </p>
          </div>
          <div className="stat-card">
            <h3>Avg. Sale Value</h3>
            <p className="stat-number">
              ₹{filteredData.length > 0 ? 
                Math.round(filteredData.reduce((sum, sale) => 
                  sum + parseFloat(sale.planAmount.replace(/[₹,]/g, '')), 0
                ) / filteredData.length).toLocaleString() : 0}
            </p>
          </div>
        </div>

        <div className="table-container">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Agent</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    No sales data found
                  </td>
                </tr>
              ) : (
                filteredData.map((sale) => (
                  <tr key={sale._id} onClick={() => handleViewDetails(sale)} className="clickable-row">
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">{sale.fullName}</div>
                        <div className="customer-email">{sale.emailAddress}</div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div>{sale.contactNumber}</div>
                        <div className="pan-number">PAN: {sale.panNumber}</div>
                      </div>
                    </td>
                    <td>
                      <div className="address-info">
                        {sale.address}
                      </div>
                    </td>
                    <td>
                      <span className={`plan-badge ${sale.plan.toLowerCase().replace(' ', '-')}`}>
                        {sale.plan}
                      </span>
                    </td>
                    <td className="amount">{sale.planAmount}</td>
                    <td>{sale.agentName}</td>
                    <td className="date">{sale.submissionDateFormatted}</td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="action-btn view-btn" 
                          title="View Details"
                          onClick={() => handleViewDetails(sale)}
                        >
                          <FaEye />
                        </button>
                        <button 
                          className="action-btn invoice-btn" 
                          title="Generate Invoice"
                          onClick={() => handleGenerateInvoice(sale)}
                        >
                          <FaFilePdf />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </>
        )}

        {activeTab === 'ads' && adsLoading && (
          <div className="loading-container">
            <FiRefreshCw className="loading-spinner" />
            <p>Loading ads form data...</p>
          </div>
        )}

        {activeTab === 'ads' && !adsLoading && adsError && (
          <div className="error-container">
            <p>Error: {adsError}</p>
            <button onClick={handleRefresh} className="retry-btn">
              <FiRefreshCw /> Retry
            </button>
          </div>
        )}

        {activeTab === 'ads' && !adsLoading && !adsError && (
          <>
            <div className="filters-section">
              <div className="search-container">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by name, email, mobile, market, or capital..."
                  value={adsSearchTerm}
                  onChange={(e) => setAdsSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="filter-controls">
                <div className="filter-group">
                  <label>Sort by date:</label>
                  <select
                    value={adsSortOrder}
                    onChange={(e) => setAdsSortOrder(e.target.value)}
                    className="filter-select"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="stats-container">
              <div className="stat-card">
                <h3>Total Registrations</h3>
                <p className="stat-number">{filteredAdsData.length}</p>
              </div>
              <div className="stat-card">
                <h3>All Records</h3>
                <p className="stat-number">{adsData.length}</p>
              </div>
            </div>

            <div className="table-container">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile Number</th>
                    <th>Market</th>
                    <th>Capital</th>
                    <th>Registration Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdsData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-data">
                        No ads form data found
                      </td>
                    </tr>
                  ) : (
                    filteredAdsData.map((ad) => (
                      <tr key={ad._id}>
                        <td>
                          <div className="customer-name">{ad.name}</div>
                        </td>
                        <td>
                          <div className="customer-email">{ad.email}</div>
                        </td>
                        <td>{ad.mobileNumber}</td>
                        <td>{ad.market || '-'}</td>
                        <td>{ad.capital || '-'}</td>
                        <td className="date">{formatAdsDate(ad.registrationDate)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {isModalOpen && selectedSale && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sale Details</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-section">
                  <h3>Customer Information</h3>
                  <div className="detail-item">
                    <label>Full Name:</label>
                    <span>{selectedSale.fullName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email Address:</label>
                    <span>{selectedSale.emailAddress}</span>
                  </div>
                  <div className="detail-item">
                    <label>Contact Number:</label>
                    <span>{selectedSale.contactNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>PAN Number:</label>
                    <span>{selectedSale.panNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>Address:</label>
                    <span>{selectedSale.address}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Transaction Information</h3>
                  <div className="detail-item">
                    <label>Sales ID:</label>
                    <span>{selectedSale.salesId}</span>
                  </div>
                  <div className="detail-item">
                    <label>Plan:</label>
                    <span className={`plan-badge ${selectedSale.plan.toLowerCase().replace(' ', '-')}`}>
                      {selectedSale.plan}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Amount:</label>
                    <span className="amount">{selectedSale.planAmount}</span>
                  </div>
                  <div className="detail-item">
                    <label>Agent Name:</label>
                    <span>{selectedSale.agentName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Transaction Date:</label>
                    <span>{selectedSale.submissionDateFormatted}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>System Information</h3>
                  <div className="detail-item">
                    <label>Created At:</label>
                    <span>{new Date(selectedSale.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Updated:</label>
                    <span>{new Date(selectedSale.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isExportModalOpen && (
        <div className="modal-overlay" onClick={handleCloseExportModal}>
          <div className="modal-content export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Export Data with Filters</h2>
              <button className="modal-close-btn" onClick={handleCloseExportModal}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="export-filters">
                <div className="filter-row">
                  <label>Search:</label>
                  <input
                    type="text"
                    placeholder="Search by name, email, agent..."
                    value={exportFilters.searchTerm}
                    onChange={(e) => setExportFilters({...exportFilters, searchTerm: e.target.value})}
                    className="filter-input"
                  />
                </div>

                <div className="filter-row">
                  <label>Plan:</label>
                  <select
                    value={exportFilters.planFilter}
                    onChange={(e) => setExportFilters({...exportFilters, planFilter: e.target.value})}
                    className="filter-select"
                  >
                    <option value="all">All Plans</option>
                    {uniquePlans.map(plan => (
                      <option key={plan} value={plan}>{plan}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-row">
                  <label>Date Range:</label>
                  <select
                    value={exportFilters.dateFilter}
                    onChange={(e) => setExportFilters({...exportFilters, dateFilter: e.target.value})}
                    className="filter-select"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>

                <div className="filter-row">
                  <label>Month:</label>
                  <select
                    value={exportFilters.monthFilter}
                    onChange={(e) => setExportFilters({...exportFilters, monthFilter: e.target.value})}
                    className="filter-select"
                  >
                    <option value="">All Months</option>
                    {monthNames.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-row">
                  <label>Year:</label>
                  <select
                    value={exportFilters.yearFilter}
                    onChange={(e) => setExportFilters({...exportFilters, yearFilter: e.target.value})}
                    className="filter-select"
                  >
                    <option value="">All Years</option>
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-row">
                  <label style={{marginBottom: '0.5rem'}}>Select Columns to Export:</label>
                  <div className="column-checkboxes">
                    {availableColumns.map(col => (
                      <label key={col.key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={exportFilters.selectedColumns[col.key]}
                          onChange={(e) => setExportFilters({
                            ...exportFilters,
                            selectedColumns: {
                              ...exportFilters.selectedColumns,
                              [col.key]: e.target.checked
                            }
                          })}
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseExportModal}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleExportWithFilters}>
                <FiDownload /> Export Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
